/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.cardSelectors = this.querySelectorAll('[data-card-selector="true"]');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.updateCardOptions();

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateCardOptions();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (this.syncingOptionSelectors) return;

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (!this.variant && !this.preSelection && !this.syncingOptionSelectors) {
        const changedSelectorIndex = this.getChangedSelectorIndex(evt);
        const matchingVariant = this.findMatchingVariant(changedSelectorIndex);

        if (matchingVariant) {
          this.syncSelectorsToVariant(matchingVariant, changedSelectorIndex);
          this.selectedOptions = this.getSelectedOptions();
          this.variant = this.getSelectedVariant();
          this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
        }
      }

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updateCardOptions();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Gets the index of the selector that triggered a change.
     * @param {object} evt - Event object.
     * @returns {number}
     */
    getChangedSelectorIndex(evt) {
      const selector = evt?.target?.closest?.('.option-selector');
      if (!selector) return -1;
      return Array.from(this.optionSelectors).indexOf(selector);
    }

    /**
     * Finds the closest matching variant based on selected options.
     * @param {number} changedSelectorIndex - Index of selector changed by the user.
     * @returns {?object}
     */
    findMatchingVariant(changedSelectorIndex) {
      const hasSelection = this.selectedOptions.some((option) => option !== null);
      if (!hasSelection) return null;

      const candidates = this.data.product.variants.filter((variant) => {
        if (changedSelectorIndex >= 0) {
          const changedOption = this.selectedOptions[changedSelectorIndex];
          if (changedOption === null || variant.options[changedSelectorIndex] !== changedOption) {
            return false;
          }
        }

        for (let i = 0; i < this.selectedOptions.length; i += 1) {
          if (i > changedSelectorIndex && changedSelectorIndex >= 0) continue;
          if (this.selectedOptions[i] !== null && variant.options[i] !== this.selectedOptions[i]) {
            return false;
          }
        }

        return true;
      });

      return candidates.find((variant) => variant.available) || candidates[0] || null;
    }

    /**
     * Synchronizes displayed selectors to match a valid variant.
     * @param {object} variant - Variant to sync to.
     * @param {number} changedSelectorIndex - Index of selector changed by the user.
     */
    syncSelectorsToVariant(variant, changedSelectorIndex) {
      this.syncingOptionSelectors = true;

      this.optionSelectors.forEach((selector, selectorIndex) => {
        if (selectorIndex === changedSelectorIndex) return;

        const optionValue = variant.options[selectorIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          const customSelect = selector.querySelector('custom-select');
          if (!customSelect) return;

          const optionEl = Array.from(customSelect.querySelectorAll('.js-option')).find(
            (option) => option.dataset.value === optionValue
          );
          if (!optionEl || customSelect.selectedOption === optionEl) return;

          customSelect.selectOption(optionEl);
        } else {
          const input = Array.from(selector.querySelectorAll('.js-option')).find(
            (option) => option.value === optionValue
          );
          if (input) input.checked = true;
        }
      });

      this.syncingOptionSelectors = false;
    }

    /**
     * Updates card-based option selectors with the best matching variant data.
     */
    updateCardOptions() {
      if (!this.cardSelectors.length) return;

      this.cardSelectors.forEach((selector) => {
        const optionIndex = Number.parseInt(selector.dataset.optionIndex, 10);
        if (Number.isNaN(optionIndex)) return;

        selector.querySelectorAll('.js-option').forEach((option) => {
          const cardVariant = this.findVariantForCard(optionIndex, option.value);
          this.updateCardOption(option, cardVariant);
        });
      });
    }

    /**
     * Finds the most relevant variant for a card option value.
     * @param {number} optionIndex - Index of the option this card selector controls.
     * @param {string} optionValue - Option value represented by the card.
     * @returns {?object}
     */
    findVariantForCard(optionIndex, optionValue) {
      const candidates = this.data.product.variants.filter(
        (variant) => variant.options[optionIndex] === optionValue
      );

      if (!candidates.length) return null;

      const matchingCandidates = candidates.filter((variant) => (
        this.selectedOptions.every((selectedOption, selectedIndex) => {
          if (selectedIndex === optionIndex || selectedOption === null) return true;
          return variant.options[selectedIndex] === selectedOption;
        })
      ));

      const variantsToUse = matchingCandidates.length ? matchingCandidates : candidates;
      return variantsToUse.find((variant) => variant.available) || variantsToUse[0] || null;
    }

    /**
     * Updates a single card option label.
     * @param {HTMLInputElement} option - Option input.
     * @param {?object} variant - Matching variant.
     */
    updateCardOption(option, variant) {
      const label = option.nextElementSibling;
      if (!label) return;

      const formatted = variant ? this.data.formatted[variant.id] : null;
      const compareAtPrice = label.querySelector('.js-variant-card-compare');
      const currentPrice = label.querySelector('.js-variant-card-price');
      const currentPriceTtc = label.querySelector('.js-variant-card-price-ttc');
      const priceHt = label.querySelector('.js-variant-card-price-ht');
      const discount = label.querySelector('.js-variant-card-discount');
      const unitPrice = label.querySelector('.js-variant-card-unit-price');
      const imageWrapper = label.querySelector('.js-variant-card-image-wrapper');
      const image = label.querySelector('.js-variant-card-image');

      if (currentPrice) {
        currentPrice.innerHTML = formatted?.price || '';
      }

      if (currentPriceTtc) {
        currentPriceTtc.hidden = !formatted?.price;
      }

      if (priceHt) {
        priceHt.innerHTML = formatted?.priceHt ? `${formatted.priceHt} HT` : '';
      }

      if (compareAtPrice) {
        compareAtPrice.innerHTML = formatted?.compareAtPrice || '';
        compareAtPrice.hidden = !formatted?.compareAtPrice;
      }

      if (discount) {
        discount.textContent = formatted?.discountPercent ? `-${formatted.discountPercent}%` : '';
        discount.hidden = !formatted?.discountPercent;
      }

      if (unitPrice) {
        unitPrice.textContent = this.getPricePer100ml(option.value, formatted?.priceValue);
        unitPrice.hidden = !unitPrice.textContent;
      }

      if (imageWrapper && image) {
        if (formatted?.featuredMedia?.src) {
          image.src = formatted.featuredMedia.src;
          image.alt = formatted.featuredMedia.alt || variant?.title || '';
          image.hidden = false;
          imageWrapper.classList.remove('is-empty');
        } else {
          image.removeAttribute('src');
          image.alt = '';
          image.hidden = true;
          imageWrapper.classList.add('is-empty');
        }
      }
    }

    /**
     * Formats the price per 100ml based on an option value.
     * @param {string} optionValue - Variant option value.
     * @param {?number} priceValue - Variant price in cents.
     * @returns {string}
     */
    getPricePer100ml(optionValue, priceValue) {
      const volumeInMl = this.parseVolumeInMl(optionValue);
      if (!volumeInMl || !priceValue) return '';

      const pricePer100Ml = Math.round((priceValue / volumeInMl) * 100);
      return `${this.formatMoney(pricePer100Ml)} / 100ml`;
    }

    /**
     * Parses an option value and returns its volume in milliliters.
     * @param {string} optionValue - Variant option value.
     * @returns {?number}
     */
    parseVolumeInMl(optionValue) {
      if (!optionValue) return null;

      const match = optionValue.toString().trim().toLowerCase().replace(',', '.')
        .match(/(\d+(?:\.\d+)?)\s*(ml|cl|l)\b/);

      if (!match) return null;

      const amount = Number.parseFloat(match[1]);
      if (!Number.isFinite(amount) || amount <= 0) return null;

      const unitMultipliers = {
        ml: 1,
        cl: 10,
        l: 1000
      };

      return amount * unitMultipliers[match[2]];
    }

    /**
     * Formats money using the shop money format.
     * @param {number} cents - Price in cents.
     * @returns {string}
     */
    formatMoney(cents) {
      const format = theme?.settings?.moneyFormat || '${{amount}}';
      const amount = Math.round(Number(cents)) / 100;

      const formatWithDelimiters = (number, precision = 2, thousands = ' ', decimal = ',') => {
        if (Number.isNaN(number) || number === null) return '0';

        const fixed = number.toFixed(precision);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

        return parts.length > 1 ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      };

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatWithDelimiters(amount, 2, '.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, formatWithDelimiters(amount, 0, '.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, formatWithDelimiters(amount, 0, ',', '.'))
        .replace(/\{\{\s*amount\s*\}\}/, formatWithDelimiters(amount, 2, ',', '.'));
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const formatted = this.data.formatted[this.variant.id];
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const priceTtcEl = priceCurrentEl.querySelector('.price__ttc');
        const priceHtEl = this.price.querySelector('.price_HT');
        const priceHtWrapperEl = this.price.querySelector('.price_HT_wrapper');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = formatted.price;
        if (priceTtcEl) {
          priceCurrentEl.appendChild(priceTtcEl);
        }
        if (priceWasEl)
          priceWasEl.innerHTML = formatted.compareAtPrice || '';

        if (priceHtEl) {
          priceHtEl.innerHTML = formatted.priceHt ? `(${formatted.priceHt} HT)` : '';
        }
        if (priceHtWrapperEl) {
          priceHtWrapperEl.hidden = !formatted.priceHt;
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = formatted.unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
